import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError, isValidEmail } from "@/lib/api";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { useGoogleAuthEnabled } from "@/lib/authConfig";
import { PasswordField } from "@/components/PasswordField";
import { PasswordInput } from "@/components/PasswordInput";
import { US_STATES } from "@/lib/constants";
import { Loader2, User, Building2, ArrowLeft, RefreshCw, Check, X, UserPlus } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { LOGO, AUTH_PHOTOS } from "@/assets";

// Same 2x2 split-grid photos as /join and /login — from the hashed asset registry (kept in sync).
const PHOTOS = AUTH_PHOTOS;

// Locked dark-theme tokens for this page (readable against the near-black panel).
const HEADING = "text-[#E8E8EA]";
const LABEL = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";

export default function Register() {
  const [step, setStep] = useState("type"); // "type" | "details"
  const [accountType, setAccountType] = useState(null); // "individual" | "business"
  const [form, setForm] = useState({
    email: "", password: "", zip_code: "", city: "", state: "",
    first_name: "", last_name: "",
    business_name: "", business_contact_email: "",
  });
  const [errors, setErrors] = useState({});
  const [pwValid, setPwValid] = useState(false);
  const [pwShake, setPwShake] = useState(0);   // bumps on a failed submit to replay the checklist shake
  const [attempted, setAttempted] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [handle, setHandle] = useState("");
  const [handleSuggested, setHandleSuggested] = useState(false); // true while the field holds an untouched auto-suggestion
  const [handleStatus, setHandleStatus] = useState(null); // {checking, valid, available, reason}
  const [handleBusy, setHandleBusy] = useState(false);
  const [general, setGeneral] = useState("");
  const googleEnabled = useGoogleAuthEnabled();
  const { businessFeaturesEnabled, applyAuth } = useAuth();
  const navigate = useNavigate();
  const [pendingPlan, setPendingPlan] = useState(null);
  const [inviteVouch, setInviteVouch] = useState(null); // { code, inviter_username }
  // Membership cap — when closed, the register form is replaced by a waitlist form.
  const [capClosed, setCapClosed] = useState(false);
  const [wl, setWl] = useState({ email: "", name: "", how_heard: "" });
  const [wlBusy, setWlBusy] = useState(false);
  const [wlDone, setWlDone] = useState(false);
  const [wlErr, setWlErr] = useState("");

  // Preserve subscribe-intent from /pricing (?plan=&interval=) across the verify → login gap.
  // Membership cap: if new registration is closed, swap the form for a waitlist form.
  useEffect(() => {
    api.get("/membership/cap-status").then(({ data }) => {
      if (data && data.open === false) setCapClosed(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const plan = (q.get("plan") || "").toLowerCase();
    const interval = (q.get("interval") || "").toLowerCase() === "annual" ? "year" : "month";
    if (["standard", "preferred", "elite"].includes(plan)) {
      const intent = { tier: plan, interval };
      localStorage.setItem("os_pending_plan", JSON.stringify(intent));
      setPendingPlan(intent);
    }
    // Member-invite: a code arrives via ?ref= or the /i/<code> landing (stored in localStorage).
    const code = (q.get("ref") || localStorage.getItem("os_invite_code") || "").trim().toUpperCase();
    if (code) {
      localStorage.setItem("os_invite_code", code);
      import("@/lib/analytics").then(({ peekVisitorId }) => {
        api.get(`/invites/resolve/${code}`, { params: { visitor_id: peekVisitorId() || undefined } })
          .then(({ data }) => { if (data.valid) setInviteVouch({ code, inviter_username: data.inviter_username }); })
          .catch(() => {});
      });
    }
  }, []);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const onZip = async (e) => {
    const zip = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
    setForm((f) => ({ ...f, zip_code: zip }));
    setErrors((p) => ({ ...p, zip_code: undefined }));
    if (zip.length === 5) {
      setZipBusy(true);
      try {
        const { data } = await api.get(`/geo/zip/${zip}`);
        setForm((f) => ({ ...f, city: data.city, state: data.state }));
      } catch {
        setErrors((p) => ({ ...p, zip_code: "We couldn't find that ZIP code." }));
      } finally {
        setZipBusy(false);
      }
    }
  };

  const onPwValidity = useCallback((v) => setPwValid(v), []);

  const fetchSuggestion = useCallback(async () => {
    setHandleBusy(true);
    try {
      const { data } = await api.get("/auth/handle-suggestion");
      if (data?.handle) { setHandle(data.handle); setHandleSuggested(true); }
    } catch {
      /* keep the current suggestion on failure */
    } finally {
      setHandleBusy(false);
    }
  }, []);

  const suggest = async () => {
    if (handleBusy) return;
    await fetchSuggestion(); // fills the field as a starting point; the live check runs on change
  };

  // Normalize handle input to the allowed shape as the user types.
  const onHandle = (e) => {
    const v = (e.target.value || "").toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
    setHandle(v);
    setHandleSuggested(false); // the moment the user edits, it's their chosen handle — not a suggestion
  };

  // Debounced live availability check against ALL existing handles + reserved/system names.
  const handleDebounce = useRef();
  useEffect(() => {
    clearTimeout(handleDebounce.current);
    const h = (handle || "").trim().toLowerCase();
    if (!h) { setHandleStatus(null); return; }
    if (!/^[a-z][a-z0-9_]{2,19}$/.test(h)) {
      setHandleStatus({ checking: false, valid: false, available: false, reason: "3–20 characters, letters/numbers/underscore, starting with a letter." });
      return;
    }
    setHandleStatus({ checking: true });
    handleDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/handle-check", { params: { u: h } });
        setHandleStatus({ checking: false, valid: data.valid, available: data.available, reason: data.reason });
      } catch {
        setHandleStatus({ checking: false, valid: false, available: false, reason: "Couldn't check that handle right now — try again." });
      }
    }, 400);
    return () => clearTimeout(handleDebounce.current);
  }, [handle]);

  const chooseType = (t) => {
    setAccountType(t);
    setStep("details");
    setGeneral("");
    if (!handle) fetchSuggestion();
  };

  const isBusiness = accountType === "business";

  const canSubmit =
    isValidEmail(form.email) &&
    /^\d{5}$/.test(form.zip_code) &&
    !errors.zip_code &&
    pwValid &&
    form.password.length > 0 &&
    confirm === form.password &&
    handleStatus?.available === true &&
    (!isBusiness || form.business_name.trim().length >= 2) &&
    (isBusiness || (form.first_name.trim().length >= 2 && form.last_name.trim().length >= 2)) &&
    !busy;

  // First unmet requirement, shown under the button once the user has tried to submit.
  const blockerMsg = (() => {
    if (canSubmit) return "";
    if (!isBusiness && (form.first_name.trim().length < 2 || form.last_name.trim().length < 2)) return "Enter your first and last name.";
    if (isBusiness && form.business_name.trim().length < 2) return "Enter your legal business name.";
    if (!isValidEmail(form.email)) return "Enter a valid email address.";
    if (!/^\d{5}$/.test(form.zip_code)) return "Enter a valid 5-digit ZIP code.";
    if (form.password.length < 12) return "Your password needs at least 12 characters.";
    if (!pwValid) return "Your password doesn't meet the strength requirements yet — see the checklist above.";
    if (confirm !== form.password) return "Passwords don't match.";
    if (!handleStatus?.available) return "Pick an available handle.";
    return "Complete the highlighted fields to continue.";
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setGeneral("");
    setAttempted(true);
    const errs = {};
    if (isBusiness && form.business_name.trim().length < 2) errs.business_name = "Enter your legal business name.";
    if (!isBusiness && form.first_name.trim().length < 2) errs.first_name = "Enter your first name.";
    if (!isBusiness && form.last_name.trim().length < 2) errs.last_name = "Enter your last name.";
    if (!isValidEmail(form.email)) errs.email = "Enter a valid email address.";
    if (!/^\d{5}$/.test(form.zip_code)) errs.zip_code = "Enter a valid 5-digit ZIP code.";
    setErrors(errs);
    if (confirm !== form.password) setConfirmTouched(true);
    // Password is the most common silent blocker — flash the checklist red + scroll it into view.
    if (!pwValid) {
      setPwShake((n) => n + 1);
      document.getElementById("reg-password")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (confirm !== form.password) return;
    if (Object.keys(errs).length || !pwValid || !handleStatus?.available) return;
    setBusy(true);
    try {
      // Response is intentionally identical whether or not the email is taken.
      const { peekVisitorId } = await import("@/lib/analytics");
      const { data } = await api.post("/auth/register", {
        account_type: accountType,
        email: form.email,
        password: form.password,
        zip_code: form.zip_code,
        city: form.city,
        state: form.state,
        preferred_handle: handle || undefined,
        invite_code: (localStorage.getItem("os_invite_code") || "").trim().toUpperCase() || undefined,
        visitor_id: peekVisitorId() || undefined,
        ...(isBusiness ? {
          business_name: form.business_name,
          business_contact_email: form.business_contact_email || undefined,
        } : {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
        }),
      });
      setHandle(data?.handle || "");
      localStorage.removeItem("os_invite_code");
      // Business accounts go straight into the authenticated 3-step application. Auto-login via the
      // normal login path (reuses the existing token minting). If the email already existed, login
      // fails and we fall through to the neutral success screen — no enumeration signal either way.
      if (isBusiness) {
        try {
          const { data: lg } = await api.post("/auth/login", { email: form.email, password: form.password });
          applyAuth(lg.token, lg.user);
          navigate("/business/apply");
          return;
        } catch {
          /* existing business email or login blocked — show the neutral success screen */
        }
      }
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "membership_capped") {
        setCapClosed(true);
        setWl((w) => ({ ...w, email: form.email || w.email }));
        return;
      }
      setGeneral(formatError(detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setWlErr("");
    if (!isValidEmail(wl.email)) { setWlErr("Enter a valid email address."); return; }
    setWlBusy(true);
    try {
      await api.post("/membership/waitlist", { email: wl.email, name: wl.name, how_heard: wl.how_heard });
      setWlDone(true);
    } catch (err) {
      setWlErr(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setWlBusy(false);
    }
  };

  const inputCls = (k) =>
    `mt-1 w-full bg-[#0D0E0F] border ${errors[k] ? "border-blaze" : "border-[#3E3F42] focus:border-[#57682A]"} outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] transition-colors`;

  const TypeCard = ({ type, icon: Icon, title, blurb, testId }) => (
    <button
      type="button"
      onClick={() => chooseType(type)}
      data-testid={testId}
      className="group text-left border border-[#3E3F42] hover:border-[#57682A] bg-[#0D0E0F] hover:bg-olive/5 p-5 transition-colors focus:outline-none focus:border-[#57682A]"
    >
      <Icon className="w-6 h-6 text-olive-bright mb-3" strokeWidth={1.8} aria-hidden="true" />
      <p className="font-heading text-lg font-bold uppercase tracking-tight text-[#E8E8EA]">{title}</p>
      <p className="mt-1 text-sm text-[#8B8D90] font-body leading-relaxed">{blurb}</p>
    </button>
  );

  return (
    <div className="min-h-screen bg-black text-foreground" data-testid="register-page">
      <Navbar />
      <div className="flex flex-col lg:flex-row">
      <div className="hidden lg:grid lg:w-1/2 lg:min-h-[calc(100vh-4rem)] grid-cols-2 grid-rows-2 gap-3 p-3 bg-black">
        {PHOTOS.map((src, i) => (
          <div key={i} className="overflow-hidden bg-[#0a0b09]"><img src={src} alt="" className="w-full h-full object-cover" /></div>
        ))}
      </div>

      <div className="grid lg:hidden grid-cols-2 gap-2 p-2 bg-black" data-testid="register-mobile-grid">
        {PHOTOS.map((src, i) => (
          <div key={i} className="overflow-hidden bg-[#0a0b09] h-28 sm:h-40"><img src={src} alt="" className="w-full h-full object-cover" /></div>
        ))}
      </div>

      <div className="lg:w-1/2 lg:min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-8 bg-black">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-block mb-6">
            <img src={LOGO} alt="OpticSwap" className="h-16 w-auto" />
          </Link>

          {capClosed ? (
            wlDone ? (
              <div data-testid="waitlist-success" className="border border-olive/40 bg-olive/5 p-5">
                <p className={`font-heading text-2xl font-bold ${HEADING}`}>You're on the list.</p>
                <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
                  Thanks — we'll email you at <span className="text-foreground">{wl.email}</span> when membership opens.
                </p>
                <Link to="/" className="mt-4 inline-flex w-full items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">Back to OpticSwap</Link>
              </div>
            ) : (
              <form onSubmit={submitWaitlist} data-testid="membership-waitlist-form" className="border border-[#3E3F42] bg-[#0D0E0F] p-5 space-y-4">
                <div>
                  <p className={`font-heading text-2xl font-bold ${HEADING}`}>Membership is full</p>
                  <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
                    OpticSwap is at capacity. Leave your email and we will let you know when membership opens.
                  </p>
                </div>
                {wlErr && <div data-testid="waitlist-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{wlErr}</div>}
                <div>
                  <label className={LABEL}>Email</label>
                  <input type="email" value={wl.email} onChange={(e) => setWl((w) => ({ ...w, email: e.target.value }))} className={inputCls("wl_email")} placeholder="you@example.com" data-testid="waitlist-email" />
                </div>
                <div>
                  <label className={LABEL}>Name</label>
                  <input value={wl.name} onChange={(e) => setWl((w) => ({ ...w, name: e.target.value }))} className={inputCls("wl_name")} placeholder="Your name" data-testid="waitlist-name" />
                </div>
                <div>
                  <label className={LABEL}>How did you hear about OpticSwap?</label>
                  <input value={wl.how_heard} onChange={(e) => setWl((w) => ({ ...w, how_heard: e.target.value }))} className={inputCls("wl_how")} placeholder="Reddit, a friend, a forum…" data-testid="waitlist-how-heard" />
                </div>
                <button type="submit" disabled={wlBusy} data-testid="waitlist-submit" className="w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
                  {wlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Join the waitlist
                </button>
                <p className="text-xs text-muted-foreground font-body">Already a member? <Link to="/login" className="text-olive-bright underline">Log in</Link>.</p>
              </form>
            )
          ) : submitted ? (
            <div data-testid="register-success" className="border border-olive/40 bg-olive/5 p-5">
              <p className={`font-heading text-2xl font-bold ${HEADING}`}>Welcome to OpticSwap.</p>
              <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
                If that address is available, your account is ready — check your inbox to confirm it, then log in.
              </p>
              {handle && (
                <div className="mt-4 border border-[#3E3F42] bg-[#0D0E0F] p-3" data-testid="register-success-handle">
                  <p className={LABEL}>Your handle</p>
                  <p className="font-heading text-xl text-[#E8E8EA] mt-0.5">@{handle}</p>
                  <p className="mt-1.5 text-xs text-[#8B8D90] font-body leading-relaxed">
                    This is how buyers will see you across the marketplace. Keep it or change it anytime in Settings.
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3 font-body leading-relaxed">
                We keep signup responses deliberately vague so nobody can use this page to discover who has an account here.
              </p>
              {pendingPlan && (
                <p className="text-xs text-volt mt-3 font-mono uppercase tracking-wide" data-testid="register-plan-intent">
                  After you confirm your email and log in, we'll take you straight to checkout for the {pendingPlan.tier} plan.
                </p>
              )}
              <Link to="/login" data-testid="register-success-login" className="mt-4 inline-flex w-full items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
                Log In
              </Link>
            </div>
          ) : step === "type" ? (
            <div data-testid="register-type-step">
              {inviteVouch && (
                <div className="mb-4 border border-olive/40 bg-olive/5 px-4 py-3 flex items-center gap-2" data-testid="register-invite-vouch">
                  <UserPlus className="w-4 h-4 text-olive-bright shrink-0" />
                  <span className="font-mono text-xs uppercase text-secondary-foreground">Invited by <span className="text-olive-bright">@{inviteVouch.inviter_username}</span></span>
                </div>
              )}
              <h1 className={`font-heading text-4xl font-bold uppercase tracking-tight ${HEADING}`}>Create Your Account</h1>
              <p className="text-sm text-[#8B8D90] mt-1 font-body">First — who's signing up?</p>

              <div className="mt-6 space-y-3">
                <TypeCard
                  type="individual" icon={User} testId="register-type-individual"
                  title="Individual"
                  blurb="Buy, sell and trade your own optics in a verified-only marketplace. Every member is ID-verified — pick a membership when you're ready to buy or sell."
                />
                {businessFeaturesEnabled && (
                  <TypeCard
                    type="business" icon={Building2} testId="register-type-business"
                    title="Business"
                    blurb="Licensed FFL/SOT dealers and retailers. List firearms, optics and accessories under a verified business account. Separate from any individual account you may already have."
                  />
                )}
              </div>

              {googleEnabled && (
                <>
                  <div className="my-5 flex items-center gap-3 text-xs font-mono text-muted-foreground uppercase">
                    <div className="flex-1 h-px bg-sniper" /> or <div className="flex-1 h-px bg-sniper" />
                  </div>
                  <GoogleAuthButton testId="google-register-btn" />
                  <p className="mt-2 text-[11px] font-mono text-muted-foreground">Google sign-in creates an Individual account.</p>
                </>
              )}

              <p className="mt-6 text-sm text-secondary-foreground font-body">
                Already have an account? <Link to="/login" data-testid="register-login-link" className="text-olive-bright hover:text-volt">Log in</Link>
              </p>
            </div>
          ) : (
            <div data-testid="register-details-step">
              <button type="button" onClick={() => setStep("type")} data-testid="register-back-to-type"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide text-[#8B8D90] hover:text-[#E8E8EA] transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Change account type
              </button>

              <h1 className={`font-heading text-4xl font-bold uppercase tracking-tight ${HEADING}`}>Create Your Account</h1>
              <div className="mt-2 inline-flex items-center gap-1.5 border border-[#3E3F42] bg-[#0D0E0F] px-2.5 py-1" data-testid="register-account-type-chip">
                {isBusiness ? <Building2 className="w-3.5 h-3.5 text-olive-bright" /> : <User className="w-3.5 h-3.5 text-olive-bright" />}
                <span className="font-mono text-[11px] uppercase tracking-wide text-[#E8E8EA]">{isBusiness ? "Business account" : "Individual account"}</span>
              </div>

              <form onSubmit={submit} noValidate className="space-y-4 mt-5">
                {general && <div data-testid="register-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{general}</div>}

                {!isBusiness && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="reg-first-name" className={LABEL}>First name</label>
                      <input id="reg-first-name" data-testid="register-first-name" value={form.first_name} onChange={set("first_name")} className={inputCls("first_name")} placeholder="e.g. Jordan" autoComplete="given-name" />
                      {errors.first_name && <p data-testid="register-first-name-error" className="mt-1 text-xs font-mono text-blaze">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="reg-last-name" className={LABEL}>Last name</label>
                      <input id="reg-last-name" data-testid="register-last-name" value={form.last_name} onChange={set("last_name")} className={inputCls("last_name")} placeholder="e.g. Reyes" autoComplete="family-name" />
                      {errors.last_name && <p data-testid="register-last-name-error" className="mt-1 text-xs font-mono text-blaze">{errors.last_name}</p>}
                    </div>
                    <p className="col-span-2 -mt-1 text-xs text-[#6B6D70] font-body leading-relaxed">
                      Your real name stays private — it's never shown publicly. Buyers see only your @handle.
                    </p>
                  </div>
                )}

                {isBusiness && (
                  <>
                    <div>
                      <label htmlFor="reg-biz-name" className={LABEL}>Legal business name</label>
                      <input id="reg-biz-name" data-testid="register-business-name" value={form.business_name} onChange={set("business_name")} className={inputCls("business_name")} placeholder="e.g. OpticSwap, LLC" />
                      {errors.business_name && <p data-testid="register-business-name-error" className="mt-1 text-xs font-mono text-blaze">{errors.business_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="reg-biz-email" className={LABEL}>Contact email <span className="normal-case text-[#6B6D70]">(optional)</span></label>
                      <input id="reg-biz-email" data-testid="register-business-email" type="email" value={form.business_contact_email} onChange={set("business_contact_email")} className={inputCls("business_contact_email")} placeholder="orders@yourshop.com" />
                    </div>
                    <div className="border border-[#3E3F42] bg-[#0D0E0F] px-3 py-2.5" data-testid="register-business-pricing-note">
                      <p className="text-xs text-[#8B8D90] font-body leading-relaxed">Next, you'll complete a short business &amp; FFL/SOT verification application. Your business is reviewed before it goes live.</p>
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="reg-email" className={LABEL}>Email</label>
                  <input id="reg-email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} className={inputCls("email")} />
                  {errors.email && <p data-testid="register-email-error" className="mt-1 text-xs font-mono text-blaze">{errors.email}</p>}
                </div>

                {/* ZIP drives City + State */}
                <div>
                  <label htmlFor="reg-zip" className={LABEL}>ZIP Code</label>
                  <div className="relative">
                    <input id="reg-zip" data-testid="register-zip" inputMode="numeric" value={form.zip_code} onChange={onZip}
                      placeholder="e.g. 90210" className={inputCls("zip_code")} />
                    {zipBusy && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                  </div>
                  {errors.zip_code
                    ? <p data-testid="register-zip-error" className="mt-1 text-xs font-mono text-blaze">{errors.zip_code}</p>
                    : <p className="mt-1 text-xs font-mono text-[#6B6D70]">Sets your city &amp; state — used for shipping and local search.</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="reg-city" className={LABEL}>City</label>
                    <input id="reg-city" data-testid="register-city" value={form.city} onChange={set("city")} className={inputCls("city")} />
                  </div>
                  <div>
                    <label htmlFor="reg-state" className={LABEL}>State</label>
                    <select id="reg-state" data-testid="register-state" value={form.state} onChange={set("state")} className={inputCls("state")}>
                      <option value="">—</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <PasswordField
                  id="reg-password"
                  testId="register-password"
                  label="Password"
                  value={form.password}
                  onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                  email={form.email}
                  name={form.business_name}
                  onValidityChange={onPwValidity}
                  highlightUnmet={attempted && !pwValid}
                  shakeKey={pwShake}
                />

                {/* Confirm password — same show/hide control; must match before submit enables */}
                <div onBlur={() => setConfirmTouched(true)}>
                  <label htmlFor="reg-password-confirm" className={LABEL}>Confirm Password</label>
                  <div className="mt-1">
                    <PasswordInput
                      id="reg-password-confirm"
                      testId="register-password-confirm"
                      inputTestId="register-password-confirm-input"
                      toggleTestId="register-password-confirm-toggle"
                      value={confirm}
                      onChange={setConfirm}
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                    />
                  </div>
                  {confirmTouched && confirm.length > 0 && confirm !== form.password && (
                    <p data-testid="register-password-mismatch" className="mt-1 text-xs font-mono text-blaze">Passwords don't match.</p>
                  )}
                  {confirm.length > 0 && confirm === form.password && (
                    <p data-testid="register-password-match" className="mt-1 text-xs font-mono text-olive-bright">Passwords match.</p>
                  )}
                </div>

                {/* Public handle — editable, live-checked against every existing handle + reserved names */}
                <div className="border border-[#3E3F42] bg-[#0D0E0F] p-3" data-testid="register-handle-field">
                  <div className="flex items-center justify-between gap-3">
                    <label htmlFor="reg-handle" className={LABEL}>Your public handle</label>
                    <button type="button" onClick={suggest} disabled={handleBusy} data-testid="register-handle-suggest"
                      className="shrink-0 inline-flex items-center gap-1.5 border border-[#3E3F42] hover:border-[#57682A] disabled:opacity-40 disabled:cursor-not-allowed text-[#E8E8EA] font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                      <RefreshCw className={`w-3.5 h-3.5 ${handleBusy ? "animate-spin" : ""}`} /> Suggest one
                    </button>
                  </div>
                  <div className={`mt-1.5 flex items-center bg-[#0D0E0F] border transition-colors ${
                    handleStatus && !handleStatus.checking
                      ? (handleStatus.available ? "border-[#57682A]" : "border-blaze")
                      : "border-[#3E3F42] focus-within:border-[#57682A]"}`}>
                    <span className="pl-3 pr-0.5 text-[#6B6D70] font-heading text-lg select-none">@</span>
                    <input id="reg-handle" data-testid="register-handle-input" value={handle} onChange={onHandle}
                      autoComplete="off" spellCheck={false} placeholder="yourhandle"
                      className={`flex-1 min-w-0 bg-transparent outline-none py-2.5 pr-2 placeholder:text-[#6B6D70] text-lg transition-colors ${handleSuggested ? "text-[#8B8D90] font-body font-normal italic" : "text-[#E8E8EA] font-heading"}`} />
                    <span className="pr-3 shrink-0 flex items-center">
                      {handleStatus?.checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid="register-handle-checking" />}
                      {handleStatus && !handleStatus.checking && handleStatus.available && <Check className="w-4 h-4 text-olive-bright" strokeWidth={3} data-testid="register-handle-ok" />}
                      {handleStatus && !handleStatus.checking && !handleStatus.available && <X className="w-4 h-4 text-blaze" strokeWidth={3} data-testid="register-handle-bad" />}
                    </span>
                  </div>
                  {handleStatus && !handleStatus.checking && !handleStatus.available && handleStatus.reason && (
                    <p data-testid="register-handle-error" className="mt-1 text-xs font-mono text-blaze">{handleStatus.reason}</p>
                  )}
                  {handleStatus?.available && (
                    <p data-testid="register-handle-available" className="mt-1 text-xs font-mono text-olive-bright">@{handle} is available.</p>
                  )}
                  <p className="mt-1.5 text-xs text-[#6B6D70] font-body leading-relaxed">
                    Anonymous by design — never your name or email. 3–20 characters, letters, numbers and underscores. Change it anytime in Settings.
                  </p>
                </div>

                <button data-testid="register-submit" type="submit" aria-disabled={!canSubmit}
                  className={`w-full font-heading font-bold uppercase text-base py-3 transition-colors ${canSubmit ? "bg-olive hover:bg-olive-bright text-white" : "bg-[#26272A] text-[#6E7175] border border-[#3A3B3E] cursor-not-allowed"}`}>
                  {busy ? "Creating..." : "Create Account"}
                </button>
                {attempted && !canSubmit && blockerMsg && (
                  <p data-testid="register-submit-blocker" className="-mt-1 text-xs font-mono text-blaze text-center">{blockerMsg}</p>
                )}
              </form>

              <p className="mt-6 text-sm text-secondary-foreground font-body">
                Already have an account? <Link to="/login" data-testid="register-login-link" className="text-olive-bright hover:text-volt">Log in</Link>
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
